import { ApiError, api } from "./client";
import type {
  ListingAgentDto,
  ListingAgentDuplicateCheckDto,
  ListingAgentPhoneType,
  ListingDetailDto,
} from "./buyers";

/**
 * Listing agents — FIND AGENT / RESEARCH (D7–D18). Wire mirror of
 * `ListingAgentResearchController` (`api/listings/{listingId}/agent-research`)
 * and `ListingAgentResearchDtos.cs`. Enums arrive as their names; fields, steps
 * and trail kinds are the server's string constants.
 *
 * <p>Viewing needs `Listings.Read`; every write needs `Listings.Manage`, and
 * START also rides the staff AI rate limit (429). Errors carry `details.code`
 * — branch on the code, never the message.</p>
 */

export type ListingAgentResearchRunStatus =
  | "Queued"
  | "Running"
  | "Completed"
  | "Partial"
  | "CapReached"
  | "Failed";

export type ListingAgentResearchConfidence =
  | "Confirmed"
  | "Likely"
  | "Possible"
  | "FirmFallback"
  | "ConfirmedByYou";

export type ListingAgentResearchOrigin = "Clef" | "Web" | "Pasted" | "OnFile";
export type ListingAgentResearchDecision = "Pending" | "Accepted" | "Rejected";
/** `PhoenixRecord`: the value is already on a Phoenix listing agent — no URL,
 *  only an excerpt naming that agent. */
export type ListingAgentResearchSourceKind = "FetchedPage" | "SearchCitation" | "Clef" | "Paste" | "PhoenixRecord";

export interface ListingAgentResearchStartFactsDto {
  displayName: string;
  rawUrl?: string | null;
  normalizedUrl?: string | null;
  marketplaceName?: string | null;
  listingReference?: string | null;
  clefLinked: boolean;
  businessName?: string | null;
  headline?: string | null;
  city?: string | null;
  state?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  revenue?: number | null;
  contactFormUrl?: string | null;
  agent?: ListingAgentDto | null;
  descriptionEmails: string[];
  descriptionPhones: string[];
  descriptionNames: string[];
}

export interface ListingAgentResearchRunSummaryDto {
  id: string;
  status: ListingAgentResearchRunStatus;
  terminalReason?: string | null;
  isActive: boolean;
  requestedByUserId: string;
  requestedByName?: string | null;
  priorRunId?: string | null;
  queuedAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  findingCount: number;
  pendingCount: number;
  acceptedUnsavedCount: number;
  savedAtUtc?: string | null;
}

export interface ListingAgentResearchWorkspaceDto {
  listingId: string;
  enabled: boolean;
  facts: ListingAgentResearchStartFactsDto;
  activeRunId?: string | null;
  latestRun?: ListingAgentResearchRunSummaryDto | null;
  runs: ListingAgentResearchRunSummaryDto[];
}

export interface ListingAgentResearchSummaryDto {
  listingId: string;
  activeRunId?: string | null;
  activeRunStatus?: ListingAgentResearchRunStatus | null;
  latestRunId?: string | null;
  findingsToReview: number;
}

export interface ListingAgentResearchUsageDto {
  searchesUsed: number;
  maxSearches: number;
  fetchesUsed: number;
  maxFetches: number;
  modelCallsUsed: number;
  maxModelCalls: number;
  clefRecordsUsed: number;
  estimatedCostUsd: number;
  costCapUsd: number;
  maxMinutes: number;
}

export interface ListingAgentResearchTrailEntryDto {
  sequence: number;
  step: string;
  kind: string;
  url?: string | null;
  query?: string | null;
  outcome?: string | null;
  label?: string | null;
  detail?: string | null;
  occurredAtUtc: string;
}

export interface ListingAgentResearchFindingSourceDto {
  kind: ListingAgentResearchSourceKind;
  url?: string | null;
  title?: string | null;
  excerpt?: string | null;
  retrievedAtUtc?: string | null;
}

export interface ListingAgentResearchMatchFactDto {
  fact: string;
  onFile?: string | null;
  result: "agrees" | "differs" | "notFound" | "notOnFile" | string;
  found?: string | null;
}

export interface ListingAgentResearchFindingDto {
  id: string;
  runId: string;
  field: string;
  value: string;
  confidence: ListingAgentResearchConfidence;
  origin: ListingAgentResearchOrigin;
  howFound?: string | null;
  decision: ListingAgentResearchDecision;
  decidedByUserId?: string | null;
  decidedAtUtc?: string | null;
  savedAtUtc?: string | null;
  createdAt: string;
  sources: ListingAgentResearchFindingSourceDto[];
  matchEvidence?: { isValidListingPage: boolean; facts: ListingAgentResearchMatchFactDto[] } | null;
  carriedFromEarlierRun: boolean;
  acceptBlockedReason?: string | null;
}

export interface ListingAgentResearchFindingGroupDto {
  field: string;
  fieldLabel: string;
  hasConflict: boolean;
  findings: ListingAgentResearchFindingDto[];
}

export interface ListingAgentResearchDeadEndDto {
  step: string;
  url?: string | null;
  what: string;
  occurredAtUtc: string;
}

export interface ListingAgentResearchSaveInfoDto {
  savedAtUtc: string;
  savedByUserId?: string | null;
  savedAgentId?: string | null;
  listingUrlReplacedFrom?: string | null;
  listingUrlReplacedTo?: string | null;
  contactFormUrlSaved?: string | null;
}

export interface ListingAgentResearchRunDto {
  id: string;
  listingId: string;
  status: ListingAgentResearchRunStatus;
  terminalReason?: string | null;
  isActive: boolean;
  requestedByUserId: string;
  requestedByName?: string | null;
  priorRunId?: string | null;
  queuedAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  usage: ListingAgentResearchUsageDto;
  error?: string | null;
  inputNotes?: string | null;
  notes?: string | null;
  listingUrlAtStart?: string | null;
  trail: ListingAgentResearchTrailEntryDto[];
  findingGroups: ListingAgentResearchFindingGroupDto[];
  deadEnds: ListingAgentResearchDeadEndDto[];
  suggestedNextSteps: string[];
  save?: ListingAgentResearchSaveInfoDto | null;
  /** `"fast"` for a run made by the name / email / phone shape, whose
   *  `findingGroups` arrive as the Name, Email, Phone and Firm rows (then any
   *  per-field groups pasted in). `null` (or absent) for an older run, shown as
   *  it always was. */
  shape?: string | null;
}

export interface ListingAgentResearchPastedFindingsPayload {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  phoneType?: ListingAgentPhoneType | null;
  firmName?: string | null;
  title?: string | null;
  city?: string | null;
  state?: string | null;
  firmGeneralEmail?: string | null;
  firmMainPhone?: string | null;
  contactFormUrl?: string | null;
  profileUrl?: string | null;
}

export interface ListingAgentResearchProposedAgentDto {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  phoneType?: ListingAgentPhoneType | null;
  firmName?: string | null;
  title?: string | null;
  city?: string | null;
  state?: string | null;
  isFirmContact: boolean;
  notesLine?: string | null;
}

export interface ListingAgentResearchFillPlanItemDto {
  field: string;
  fieldLabel: string;
  value: string;
  findingId: string;
  currentValue?: string | null;
  fillable: boolean;
  reason?: string | null;
}

export interface ListingAgentResearchUrlChangeDto {
  findingId: string;
  newUrl: string;
  currentUrl?: string | null;
  allowed: boolean;
  reason?: string | null;
  collidesWithListingId?: string | null;
  collidesWithListingName?: string | null;
}

export type ListingAgentResearchSaveMode = "CreateAgent" | "FillAgent" | "None";

export interface ListingAgentResearchSavePreviewDto {
  runId: string;
  mode: ListingAgentResearchSaveMode;
  attachedAgent?: ListingAgentDto | null;
  proposedAgent?: ListingAgentResearchProposedAgentDto | null;
  duplicateCheck?: ListingAgentDuplicateCheckDto | null;
  fillPlan: ListingAgentResearchFillPlanItemDto[];
  listingUrl?: ListingAgentResearchUrlChangeDto | null;
  contactFormUrl?: ListingAgentResearchUrlChangeDto | null;
  acceptedUnsavedCount: number;
  blockers: string[];
  /** For an attached agent: the accepted values with no field to fill, as ONE
   *  line the save may append to the agent's notes (owner ruling). */
  notesLine?: string | null;
  /** Accepted values the listing or agent already holds exactly; any save marks
   *  them saved and writes nothing for them. */
  alreadyOnFile?: ListingAgentResearchOnFileDto[] | null;
}

export interface ListingAgentResearchOnFileDto {
  findingId: string;
  field: string;
  fieldLabel: string;
  value: string;
}

export interface ListingAgentResearchSavePayload {
  createAgent: boolean;
  useExistingAgentId?: string | null;
  fillFields?: string[];
  applyListingUrl: boolean;
  applyContactFormUrl: boolean;
  addNotesLine?: boolean;
}

export interface ListingAgentResearchSaveResultDto {
  listing: ListingDetailDto;
  agent?: ListingAgentDto | null;
  run: ListingAgentResearchRunDto;
}

const base = (listingId: string) => `/listings/${encodeURIComponent(listingId)}/agent-research`;

export const listingAgentResearchApi = {
  workspace: (listingId: string) => api.get<ListingAgentResearchWorkspaceDto>(base(listingId)),
  summary: (listingId: string) => api.get<ListingAgentResearchSummaryDto>(`${base(listingId)}/summary`),
  run: (listingId: string, runId: string) =>
    api.get<ListingAgentResearchRunDto>(`${base(listingId)}/runs/${encodeURIComponent(runId)}`),
  start: (listingId: string, body: { notes?: string | null; priorRunId?: string | null }) =>
    api.post<ListingAgentResearchRunDto>(`${base(listingId)}/runs`, body),
  saveNotes: (listingId: string, runId: string, notes: string) =>
    api.post<ListingAgentResearchRunDto>(`${base(listingId)}/runs/${encodeURIComponent(runId)}/notes`, { notes }),
  addPastedFindings: (listingId: string, runId: string, body: ListingAgentResearchPastedFindingsPayload) =>
    api.post<ListingAgentResearchRunDto>(
      `${base(listingId)}/runs/${encodeURIComponent(runId)}/pasted-findings`,
      body,
    ),
  decide: (listingId: string, findingId: string, decision: "Accepted" | "Rejected") =>
    api.post<ListingAgentResearchFindingDto>(
      `${base(listingId)}/findings/${encodeURIComponent(findingId)}/decision`,
      { decision },
    ),
  savePreview: (listingId: string, runId: string) =>
    api.post<ListingAgentResearchSavePreviewDto>(
      `${base(listingId)}/runs/${encodeURIComponent(runId)}/save-preview`,
      {},
    ),
  save: (listingId: string, runId: string, body: ListingAgentResearchSavePayload) =>
    api.post<ListingAgentResearchSaveResultDto>(
      `${base(listingId)}/runs/${encodeURIComponent(runId)}/save`,
      body,
    ),
};

/** The error codes the workspace branches on (`details.code`). */
export const AGENT_RESEARCH_ERROR = {
  disabled: "agentResearchDisabled",
  httpsNotVerified: "httpsNotVerified",
  findingAlreadySaved: "findingAlreadySaved",
  conflictingAcceptedFinding: "conflictingAcceptedFinding",
  researchRunActive: "researchRunActive",
  researchAlreadySaved: "researchAlreadySaved",
  nothingToSave: "nothingToSave",
  invalidSaveRequest: "invalidSaveRequest",
  listingAgentAlreadyOnFile: "listingAgentAlreadyOnFile",
  listingUrlCollision: "listingUrlCollision",
} as const;

/** `details` of an API error, when it carries a code. */
export function agentResearchErrorDetails(
  e: unknown,
): { code?: string; existingAgentId?: string; otherListingId?: string; otherListingName?: string } | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.details as Record<string, unknown> | null | undefined;
  if (!d || typeof d !== "object") return null;
  const str = (k: string) => (typeof d[k] === "string" ? (d[k] as string) : undefined);
  return {
    code: str("code"),
    existingAgentId: str("existingAgentId"),
    otherListingId: str("otherListingId"),
    otherListingName: str("otherListingName"),
  };
}
